"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const _ = require("lodash");
class Retry {
    /**
     * @constructor
     * @param jitterMilliseconds Amount to multiply the time randomization factor. A value of 1000 will yield a max
     * jitter of 1 second. A value of 0 won't randomize at all.
     * @param ceilingMilliseconds The maximum time to wait between reattempts. Bounded by MaxRetry.
     * @param waitTimeMilliseconds The total amount of time after which no more reattempts will be made.
     * @param retries The amount of times to retry calling the function before giving  up.
     * @param growthFunction Function that is given the iteration as its input, and outputs the amount of time to wait
     * per iteration.
     * @param retryFunction Function that returns a promise that will be continually retried.
     */
    constructor(params) {
        this.params = params;
        this.reattemptCount = 0;
        this.jitter = this.params.jitterMilliseconds == null ? Retry.minDelay : this.params.jitterMilliseconds;
        this.ceiling = this.params.ceilingMilliseconds == null ? Retry.maxDelay : this.params.ceilingMilliseconds;
        this.waitTime = this.params.waitTimeMilliseconds == null ? Retry.maxWaitTime : this.params.waitTimeMilliseconds;
        this.retries = this.params.retries == null ? Retry.maxRetries : this.params.retries;
        const unboundedGrowthFunction = this.params.growthFunction == null ? () => 1000 : this.params.growthFunction;
        this.retryFunction = this.params.retryFunction;
        const outOfRangeHelper = (str) => new RangeError(`${str} out of range.`);
        const inRangeInclusive = (compare, lower, upper) => compare >= lower && compare <= upper;
        if (!inRangeInclusive(this.jitter, Retry.minJitter, Retry.maxJitter)) {
            throw outOfRangeHelper('jitter');
        }
        else if (!inRangeInclusive(this.ceiling, Retry.minDelay, Retry.maxDelay)) {
            throw outOfRangeHelper('ceiling');
        }
        else if (!inRangeInclusive(this.retries, Retry.minRetries, Retry.maxRetries)) {
            throw outOfRangeHelper('retries');
        }
        else if (!inRangeInclusive(this.waitTime, Retry.minWaitTime, Retry.maxWaitTime)) {
            throw outOfRangeHelper('waitTime');
        }
        this.growthFunction = (iteration) => {
            return _.clamp(unboundedGrowthFunction(iteration), Retry.minDelay, this.ceiling);
        };
    }
    start(_params = {}) {
        this.reset();
        this.startTime = Date.now();
        return new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
            this.tryOnFail();
        });
    }
    try() {
        this.retryFunction()
            .then(result => {
            this.reset();
            this.resolve(result);
        }).catch(_error => {
            // Reattempt failed
            this.tryOnFail();
        });
    }
    tryOnFail() {
        this.cancel();
        if ((this.reattemptCount >= this.retries) || this.exceededWaitTime()) {
            this.reset();
            this.reject("Retry: Not reattempting.");
        }
        else {
            // Let's try again
            this.timeoutId = setTimeout(() => this.try(), this.getNextTimeout());
        }
    }
    exceededWaitTime() {
        return (Date.now() - this.startTime) > this.waitTime;
    }
    getNextTimeout() {
        const random = _.random(this.jitter);
        return random + this.growthFunction(++this.reattemptCount);
    }
    reset(_params = {}) {
        this.reattemptCount = 0;
        this.cancel();
    }
    cancel() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    }
}
exports.Retry = Retry;
Retry.minDelay = 0;
Retry.maxDelay = Number.MAX_SAFE_INTEGER;
Retry.maxWaitTime = Number.MAX_SAFE_INTEGER;
Retry.minWaitTime = 0;
Retry.maxRetries = Number.MAX_SAFE_INTEGER;
Retry.minRetries = 1;
Retry.minJitter = 0;
Retry.maxJitter = Number.MAX_SAFE_INTEGER;
